import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch } from 'react-hook-form';
import { TimeSignal } from 'graphql/types';
import { useFormattedDuration } from 'modules/notes/common';

interface NotesListGroupDurationProps {
  groupKey: string;
  notes: TimeSignal[];
  totalDuration: number | undefined;
  isLabelGroupDuration?: boolean;
}

interface UseStylesProps {
  isLabelGroupDuration: boolean | undefined;
}

const useStyles = makeStyles((theme) => ({
  duration: {
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
    marginRight: ({ isLabelGroupDuration }: UseStylesProps) =>
      !isLabelGroupDuration ? theme.spacing(0.875) : undefined,
    width: theme.spacing(7)
  }
}));

const useGroupDuration = ({
  notes,
  groupKey,
  totalDayDuration
}: {
  notes: TimeSignal[];
  groupKey: string;
  totalDayDuration: number;
}): number => {
  const { control } = useFormContext();
  const notesMap = useMemo(
    () =>
      notes.reduce((acc, note) => {
        acc[`${note.date}_${note.id}`] = note;
        return acc;
      }, {} as Record<string, TimeSignal>),
    [notes]
  );
  const noteIds = useMemo(() => Object.keys(notesMap), [notesMap]);
  const fields = useMemo(() => [groupKey, ...noteIds], [noteIds, groupKey]);
  const formValues = useWatch({
    name: fields,
    control,
    defaultValue: fields.reduce(
      (acc, key) => ({ ...acc, [`${key}`]: false }),
      {} as Record<string, boolean>
    )
  }) as any;

  const values = useMemo<Record<string, boolean>>(
    () =>
      fields.reduce(
        (acc, item, index) => ({
          ...acc,
          [`${item}`]: formValues[index] ?? false
        }),
        {}
      ),
    [formValues, fields]
  );

  return useMemo(() => {
    const selectedNotes = notes.filter(
      (note) => values[`${groupKey}_${note.id}`]
    );
    if (selectedNotes.length) {
      return selectedNotes.reduce((acc, note) => acc + (note.duration ?? 0), 0);
    }
    return totalDayDuration;
  }, [groupKey, notes, totalDayDuration, values]);
};
export const NotesListGroupDuration: React.FC<NotesListGroupDurationProps> = ({
  groupKey,
  notes,
  totalDuration,
  isLabelGroupDuration
}: NotesListGroupDurationProps) => {
  const classes = useStyles({ isLabelGroupDuration });
  const duration = useGroupDuration({
    notes,
    groupKey,
    totalDayDuration: totalDuration ?? 0
  });

  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: duration
  });

  return (
    <Typography
      data-testid="notes-list-day-duration"
      className={classes.duration}
      variant="caption"
    >
      {totalDuration !== undefined && formattedDuration}
    </Typography>
  );
};

export default React.memo(NotesListGroupDuration);
